import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { AvailabilityDay } from "@autopic/shared";
import {
  DOW_LABELS,
  buildMonth,
  monthTitle,
  parseISODate,
} from "../dates";
import s from "../steps.module.css";

interface CalendarProps {
  days: AvailabilityDay[];
  selected: string | null;
  onSelect: (iso: string) => void;
}

/** Month calendar with availability dots; only in-range available days are clickable. */
export function Calendar({ days, selected, onSelect }: CalendarProps) {
  const availByDate = useMemo(() => {
    const m = new Map<string, AvailabilityDay>();
    for (const d of days) m.set(d.date, d);
    return m;
  }, [days]);

  const firstIso = days[0]?.date;
  const lastIso = days[days.length - 1]?.date;
  const initial = selected ?? firstIso ?? undefined;
  const initDate = initial ? parseISODate(initial) : new Date();

  const [view, setView] = useState({ y: initDate.getFullYear(), m: initDate.getMonth() });

  const cells = useMemo(
    () => buildMonth(view.y, view.m, availByDate),
    [view, availByDate],
  );

  // Bound month navigation to the available range.
  const firstDate = firstIso ? parseISODate(firstIso) : null;
  const lastDate = lastIso ? parseISODate(lastIso) : null;
  const canPrev =
    !firstDate ||
    view.y > firstDate.getFullYear() ||
    (view.y === firstDate.getFullYear() && view.m > firstDate.getMonth());
  const canNext =
    !lastDate ||
    view.y < lastDate.getFullYear() ||
    (view.y === lastDate.getFullYear() && view.m < lastDate.getMonth());

  function shift(delta: number) {
    setView((v) => {
      const d = new Date(v.y, v.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  }

  return (
    <div className={s.calendar}>
      <div className={s.calHead}>
        <button
          type="button"
          className={s.calNav}
          onClick={() => shift(-1)}
          disabled={!canPrev}
          aria-label="Previous month"
        >
          <ChevronLeft size={18} />
        </button>
        <span className={s.calMonth} aria-live="polite">
          {monthTitle(view.y, view.m)}
        </span>
        <button
          type="button"
          className={s.calNav}
          onClick={() => shift(1)}
          disabled={!canNext}
          aria-label="Next month"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      <div className={s.calGrid} role="grid" aria-label="Available dates">
        {DOW_LABELS.map((d) => (
          <div key={d} className={s.calDow} role="columnheader">
            {d}
          </div>
        ))}
        {cells.map((cell, i) => {
          if (!cell.iso) {
            return <div key={`pad-${i}`} className={`${s.calDay} ${s.calDayEmpty}`} aria-hidden="true" />;
          }
          const isSelected = cell.iso === selected;
          const clickable = cell.available;
          const cls = [
            s.calDay,
            clickable ? s.calDayAvail : "",
            cell.inRange && !clickable ? s.calDayDisabled : "",
            !cell.inRange ? s.calDayDisabled : "",
            isSelected ? s.calDaySelected : "",
            clickable ? s.calDayDot : "",
          ]
            .filter(Boolean)
            .join(" ");
          return (
            <button
              key={cell.iso}
              type="button"
              className={cls}
              disabled={!clickable}
              aria-pressed={isSelected}
              aria-label={cell.iso}
              onClick={() => clickable && onSelect(cell.iso!)}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}
